import { useState, useRef, useMemo } from "react"
import type { Video, Script, Voice } from "@/types"

export type VoiceRegion = "british" | "north_america"

const VOICE_REGION_TABS: { value: VoiceRegion; label: string }[] = [
  { value: "british", label: "British" },
  { value: "north_america", label: "Canadian" },
]
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Slider } from "@/components/ui/slider"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import AudioPlayer from "@/components/dashboard/videos/audio-player"

const MUSIC_STYLES = [
  { value: "elegant-ambient", label: "Elegant ambient" },
  { value: "upbeat-modern", label: "Upbeat modern" },
  { value: "cinematic-orchestral", label: "Cinematic orchestral" },
  { value: "warm-acoustic", label: "Warm acoustic" },
  { value: "soft-piano", label: "Soft piano" },
  { value: "luxury-lounge", label: "Luxury lounge" },
  { value: "bright-corporate", label: "Bright corporate" },
  { value: "chill-electronic", label: "Chill electronic" },
  { value: "inspiring-uplifting", label: "Inspiring & uplifting" },
  { value: "minimal-atmospheric", label: "Minimal atmospheric" },
] as const

export default function WizardStepAudio({
  video,
  scripts,
  voicesByRegion,
  defaultVoiceRegion,
  selectedVoice,
  voiceSpeed,
  voiceStyle,
  voiceoverVolume,
  onVoiceChange,
  onVoiceSpeedChange,
  onVoiceStyleChange,
  onVoiceoverVolumeChange,
  onMusicPromptChange,
  musicVolume,
  onMusicVolumeChange,
  voiceoverGenerating,
  voiceoverMessage,
  onGenerateVoiceover,
  onDeleteVoiceover,
  musicGenerating,
  musicMessage,
  onGenerateMusic,
  onDeleteMusic,
}: {
  video: Video
  scripts: Script[]
  voicesByRegion: Record<VoiceRegion, Voice[]>
  defaultVoiceRegion: VoiceRegion
  selectedVoice: string
  voiceSpeed: number
  voiceStyle: number
  voiceoverVolume: number
  onVoiceChange: (voiceId: string) => void
  onVoiceSpeedChange: (speed: number) => void
  onVoiceStyleChange: (style: number) => void
  onVoiceoverVolumeChange: (volume: number) => void
  onMusicPromptChange: (prompt: string) => void
  musicVolume: number
  onMusicVolumeChange: (volume: number) => void
  voiceoverGenerating: boolean
  voiceoverMessage: string
  onGenerateVoiceover: () => void
  onDeleteVoiceover: () => void
  musicGenerating: boolean
  musicMessage: string
  onGenerateMusic: () => void
  onDeleteMusic: () => void
}) {
  const activeScript = scripts.find((s) => s.active)

  return (
    <div>
      <h2 className="mb-1 font-display text-2xl font-semibold text-charcoal">
        Audio
      </h2>
      <p className="mb-6 text-warm-grey">
        Select a voice, generate voiceover, and add background music.
      </p>

      <div className="space-y-6">
        {/* Voice Selection */}
        <VoiceSection
          selectedVoice={selectedVoice}
          voicesByRegion={voicesByRegion}
          defaultRegion={defaultVoiceRegion}
          onVoiceChange={onVoiceChange}
        />

        {/* Voice Settings */}
        {selectedVoice !== "" && (
          <VoiceSettingsSection
            speed={voiceSpeed}
            style={voiceStyle}
            volume={voiceoverVolume}
            onSpeedChange={onVoiceSpeedChange}
            onStyleChange={onVoiceStyleChange}
            onVolumeChange={onVoiceoverVolumeChange}
          />
        )}

        {/* Voiceover */}
        <VoiceoverSection
          hasVoice={selectedVoice !== ""}
          script={activeScript}
          volume={voiceoverVolume}
          isGenerating={voiceoverGenerating}
          generatingMessage={voiceoverMessage}
          onGenerate={onGenerateVoiceover}
          onDelete={onDeleteVoiceover}
        />

        {/* Background Music */}
        <MusicSection
          video={video}
          voiceoverReady={activeScript?.voiceover_status === "completed"}
          onMusicPromptChange={onMusicPromptChange}
          musicVolume={musicVolume}
          onMusicVolumeChange={onMusicVolumeChange}
          isGenerating={musicGenerating}
          generatingMessage={musicMessage}
          onGenerate={onGenerateMusic}
          onDelete={onDeleteMusic}
        />
      </div>
    </div>
  )
}

function VoiceSection({
  selectedVoice,
  voicesByRegion,
  defaultRegion,
  onVoiceChange,
}: {
  selectedVoice: string
  voicesByRegion: Record<VoiceRegion, Voice[]>
  defaultRegion: VoiceRegion
  onVoiceChange: (voiceId: string) => void
}) {
  const initialRegion = useMemo<VoiceRegion>(() => {
    for (const tab of VOICE_REGION_TABS) {
      if (voicesByRegion[tab.value]?.some((v) => v.id === selectedVoice)) {
        return tab.value
      }
    }
    return defaultRegion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [activeRegion, setActiveRegion] = useState<VoiceRegion>(initialRegion)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const voices = voicesByRegion[activeRegion] ?? []

  function handlePreview(voice: Voice) {
    if (playingId === voice.id) {
      audioRef.current?.pause()
      setPlayingId(null)
      return
    }
    if (audioRef.current) audioRef.current.pause()
    const audio = new Audio(voice.preview_url)
    audioRef.current = audio
    setPlayingId(voice.id)
    audio.play()
    audio.onended = () => setPlayingId(null)
  }

  function handleTabChange(region: VoiceRegion) {
    if (region === activeRegion) return
    audioRef.current?.pause()
    setPlayingId(null)
    setActiveRegion(region)
  }

  return (
    <div className="rounded-lg border border-stone/20 bg-cream/30 p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-base font-semibold text-charcoal">Voice</h3>
        <div
          role="tablist"
          aria-label="Voice region"
          className="inline-flex rounded-md border border-stone/40 bg-white p-0.5"
        >
          {VOICE_REGION_TABS.map((tab) => {
            const isActive = activeRegion === tab.value
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => handleTabChange(tab.value)}
                className={`rounded-sm px-3 py-1 text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-charcoal text-cream"
                    : "text-warm-grey hover:text-charcoal"
                }`}
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </div>

      {voices.length === 0 ? (
        <p className="text-sm text-warm-grey">No voices available for this region. Check your ElevenLabs configuration.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2">
          {voices.map((voice) => {
            const isSelected = selectedVoice === voice.id
            return (
              <div
                key={voice.id}
                className={`flex items-center gap-3 rounded-lg border-2 p-3 transition-colors ${
                  isSelected
                    ? "border-red bg-red/5"
                    : "border-stone/30 hover:border-stone"
                }`}
              >
                <button
                  type="button"
                  onClick={() => handlePreview(voice)}
                  className={`flex size-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                    playingId === voice.id
                      ? "bg-red text-white"
                      : isSelected
                        ? "bg-red/10 text-red hover:bg-red/20"
                        : "bg-stone/30 text-warm-grey hover:bg-stone/50"
                  }`}
                >
                  {playingId === voice.id ? (
                    <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
                    </svg>
                  ) : (
                    <svg className="size-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5.14v14l11-7-11-7z" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => onVoiceChange(voice.id)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="truncate text-sm font-medium text-charcoal">{voice.name}</p>
                </button>
                {isSelected && (
                  <svg className="size-4 shrink-0 text-red" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                  </svg>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function VoiceSettingsSection({
  speed,
  style,
  volume,
  onSpeedChange,
  onStyleChange,
  onVolumeChange,
}: {
  speed: number
  style: number
  volume: number
  onSpeedChange: (speed: number) => void
  onStyleChange: (style: number) => void
  onVolumeChange: (volume: number) => void
}) {
  return (
    <div className="rounded-lg border border-stone/20 bg-cream/30 p-5">
      <h3 className="mb-4 font-display text-base font-semibold text-charcoal">Voice settings</h3>
      <div className="space-y-5">
        <div>
          <div className="mb-2 flex items-center justify-between">
            <Label>Narration speed</Label>
            <span className="text-xs tabular-nums text-warm-grey">{speed.toFixed(1)}x</span>
          </div>
          <Slider
            min={0.7}
            max={1.2}
            step={0.1}
            value={[speed]}
            onValueChange={([v]) => onSpeedChange(v)}
          />
          <div className="mt-1 flex justify-between text-[10px] text-warm-grey">
            <span>Slower</span>
            <span>Faster</span>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <Label>Expressiveness</Label>
            <span className="text-xs tabular-nums text-warm-grey">
              {style === 0 ? "Neutral" : style >= 1 ? "Expressive" : `${Math.round(style * 100)}%`}
            </span>
          </div>
          <Slider
            min={0}
            max={1}
            step={0.1}
            value={[style]}
            onValueChange={([v]) => onStyleChange(v)}
          />
          <div className="mt-1 flex justify-between text-[10px] text-warm-grey">
            <span>Neutral</span>
            <span>Expressive</span>
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <Label>Voiceover volume</Label>
            <span className="text-xs tabular-nums text-warm-grey">{Math.round(volume * 100)}%</span>
          </div>
          <Slider
            min={0}
            max={1}
            step={0.05}
            value={[volume]}
            onValueChange={([v]) => onVolumeChange(v)}
          />
          <div className="mt-1 flex justify-between text-[10px] text-warm-grey">
            <span>Silent</span>
            <span>Full</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function VoiceoverSection({
  hasVoice,
  script,
  volume,
  isGenerating,
  generatingMessage,
  onGenerate,
  onDelete,
}: {
  hasVoice: boolean
  script: Script | undefined
  volume: number
  isGenerating: boolean
  generatingMessage: string
  onGenerate: () => void
  onDelete: () => void
}) {
  const generating = isGenerating || script?.voiceover_status === "generating"

  return (
    <div className="rounded-lg border border-stone/20 bg-cream/30 p-5">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="font-display text-base font-semibold text-charcoal">Voiceover</h3>
        {script && !generating && (
          <Badge className={`text-[10px] ${voiceoverStatusStyle(script.voiceover_status)}`}>
            {voiceoverStatusLabel(script.voiceover_status)}
          </Badge>
        )}
      </div>

      {!script ? (
        <p className="text-sm text-warm-grey">Generate a script first (Step 3).</p>
      ) : generating ? (
        <GeneratingRow message={generatingMessage || "Recording voiceover..."} />
      ) : script.voiceover_status === "completed" && script.voiceover_url ? (
        <div className="space-y-3">
          <AudioPlayer
            src={script.voiceover_url}
            label="Voiceover"
            sublabel={`Script v${script.version}`}
            volume={volume}
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-warm-grey hover:text-red"
            >
              Remove
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onGenerate}
              disabled={!hasVoice}
            >
              Regenerate
            </Button>
          </div>
        </div>
      ) : !hasVoice ? (
        <p className="text-sm text-warm-grey">Select a voice above first.</p>
      ) : (
        <div className="flex flex-col items-center py-4 text-center">
          <p className="mb-3 text-sm text-warm-grey">
            {script.voiceover_status === "failed"
              ? "Voiceover generation failed. Try again."
              : "Generate a voiceover from your script."}
          </p>
          <Button
            size="sm"
            onClick={onGenerate}
            className="bg-red text-white hover:bg-deep-red"
          >
            Generate voiceover
          </Button>
        </div>
      )}
    </div>
  )
}

function MusicSection({
  video,
  voiceoverReady,
  onMusicPromptChange,
  musicVolume,
  onMusicVolumeChange,
  isGenerating,
  generatingMessage,
  onGenerate,
  onDelete,
}: {
  video: Video
  voiceoverReady: boolean
  onMusicPromptChange: (prompt: string) => void
  musicVolume: number
  onMusicVolumeChange: (volume: number) => void
  isGenerating: boolean
  generatingMessage: string
  onGenerate: () => void
  onDelete: () => void
}) {
  const [prompt, setPrompt] = useState(video.music_prompt || "")

  const generating = isGenerating || video.music_status === "generating"

  return (
    <div className="rounded-lg border border-stone/20 bg-cream/30 p-5">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="font-display text-base font-semibold text-charcoal">Background music</h3>
        {!generating && (
          <Badge className={`text-[10px] ${musicStatusStyle(video.music_status)}`}>
            {musicStatusLabel(video.music_status)}
          </Badge>
        )}
      </div>

      {!voiceoverReady && video.music_status !== "completed" ? (
        <p className="text-sm text-warm-grey">
          Generate a voiceover first so the music can match its length.
        </p>
      ) : generating ? (
        <GeneratingRow message={generatingMessage || "Composing background music..."} />
      ) : video.music_status === "completed" && video.generated_music_url ? (
        <div className="space-y-3">
          <AudioPlayer
            src={video.generated_music_url}
            label="Background music"
            sublabel="Instrumental"
            volume={musicVolume}
          />
          <MusicVolumeSlider volume={musicVolume} onChange={onMusicVolumeChange} />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="text-warm-grey hover:text-red"
            >
              Remove
            </Button>
            <Button variant="ghost" size="sm" onClick={onGenerate}>
              Regenerate
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label htmlFor="music-style">Music style</Label>
            <Select
              value={prompt || "elegant-ambient"}
              onValueChange={(v) => {
                setPrompt(v)
                onMusicPromptChange(v)
              }}
            >
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder="Choose a style" />
              </SelectTrigger>
              <SelectContent>
                {MUSIC_STYLES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <MusicVolumeSlider volume={musicVolume} onChange={onMusicVolumeChange} />
          <Button
            size="sm"
            onClick={onGenerate}
            className="bg-red text-white hover:bg-deep-red"
          >
            {video.music_status === "failed" ? "Retry music generation" : "Generate music"}
          </Button>
          {video.music_status === "failed" && (
            <p className="text-xs text-red">Music generation failed. Try again.</p>
          )}
        </div>
      )}
    </div>
  )
}

function MusicVolumeSlider({
  volume,
  onChange,
}: {
  volume: number
  onChange: (volume: number) => void
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <Label>Music volume</Label>
        <span className="text-xs tabular-nums text-warm-grey">{Math.round(volume * 100)}%</span>
      </div>
      <Slider
        min={0}
        max={1}
        step={0.05}
        value={[volume]}
        onValueChange={([v]) => onChange(v)}
      />
      <div className="mt-1 flex justify-between text-[10px] text-warm-grey">
        <span>Silent</span>
        <span>Full</span>
      </div>
    </div>
  )
}

function GeneratingRow({ message }: { message: string }) {
  return (
    <div className="flex items-center gap-4 py-2">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-stone/20">
        <svg className="size-5 animate-spin text-charcoal" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
      <p className="text-sm font-medium text-charcoal">{message}</p>
    </div>
  )
}

function voiceoverStatusLabel(status: Script["voiceover_status"]) {
  return { pending: "Not generated", generating: "Generating...", completed: "Ready", failed: "Failed" }[status]
}

function voiceoverStatusStyle(status: Script["voiceover_status"]) {
  return {
    pending: "bg-stone/60 text-slate",
    generating: "bg-gold/20 text-gold",
    completed: "bg-green-100 text-green-700",
    failed: "bg-red/10 text-red",
  }[status]
}

function musicStatusLabel(status: string) {
  return { pending: "Not generated", generating: "Generating...", completed: "Ready", failed: "Failed" }[status] || status
}

function musicStatusStyle(status: string) {
  return {
    pending: "bg-stone/60 text-slate",
    generating: "bg-gold/20 text-gold",
    completed: "bg-green-100 text-green-700",
    failed: "bg-red/10 text-red",
  }[status] || "bg-stone/60 text-slate"
}
